// Circle members beside the live map: address, last update, battery and a
// "request update" button. Members travelling together share a row
// ("Driving together · …") with their own rows beneath it.
import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { historyPath } from "../history/history";
import { addressKey } from "../live/addresses";
import {
  agoText,
  contactAfterFix,
  hereSince,
  isStale,
  isStationary,
  locationLimitText,
  phoneMayBeOff,
  sinceText,
  type MemberRow,
} from "../live/liveCircle";
import { cooldownMs, outcomeText, requestOutcome, type RequestOutcome } from "../live/requestUpdate";
import { respondersText, sosBanners } from "../live/sos";
import type { TravelGroup } from "../live/together";
import { groupTitle, sidebarEntries, stackedRows } from "../live/togetherView";
import { useAddresses } from "../live/useAddresses";
import { approximateText } from "../sharing/bubbles";
import { useUnits } from "../useUnits";
import { sosActionState, type SosActionState, type SosActions } from "../live/useSosActions";
import { MemberAvatar } from "./MemberAvatar";

/** Battery at or below this, while not charging, is highlighted (as on Android). */
const LOW_BATTERY_PCT = 15;
/** A request's outcome stays at least this long. */
const MESSAGE_MS = 10_000;

interface RequestState {
  busy: boolean;
  outcome?: RequestOutcome;
  /** The server's per-member cooldown: the button stays disabled until then (epoch ms). */
  coolUntil: number;
  /** The outcome is shown until then. */
  showUntil: number;
}

/** The state after a request settles with `outcome`, timed from now. */
function settled(outcome: RequestOutcome): RequestState & { settledAt: number } {
  const wait = cooldownMs(outcome);
  const now = Date.now();
  // The message stays while the button is disabled, and at least briefly.
  return {
    busy: false,
    outcome,
    coolUntil: wait > 0 ? now + wait : 0,
    showUntil: now + Math.max(wait, MESSAGE_MS),
    settledAt: now,
  };
}

export interface MemberSidebarProps {
  circleId: string;
  rows: readonly MemberRow[];
  /** Members travelling together, listed as one row each with their members beneath. */
  groups?: readonly TravelGroup[];
  nowMs: number;
  /** A member's user id or a group's `groupKey`. */
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** "I'm on it" for members with an open SOS. */
  sos: SosActions;
  ref?: Ref<HTMLElement>;
}

export function MemberSidebar({
  circleId,
  rows,
  groups = [],
  nowMs,
  selectedId,
  onSelect,
  sos,
  ref,
}: MemberSidebarProps) {
  const [open, setOpen] = useState(true);
  const [requests, setRequests] = useState<Readonly<Record<string, RequestState>>>({});
  const addresses = useAddresses(
    circleId,
    rows.flatMap((row) =>
      row.location ? [{ userId: row.userId, key: addressKey(row.location.lat, row.location.lon) }] : [],
    ),
    nowMs,
  );
  // Request deadlines are checked against this clock, advanced by a timer at the next one.
  const [clock, setClock] = useState(() => Date.now());
  const listRef = useRef<HTMLUListElement>(null);
  const banners = new Map(sosBanners(rows).map((banner) => [banner.userId, banner]));

  useEffect(() => {
    const next = Math.min(
      ...Object.values(requests).flatMap((r) => [r.coolUntil, r.showUntil].filter((at) => at > clock)),
    );
    if (!Number.isFinite(next)) return;
    const timer = setTimeout(() => setClock(Date.now()), Math.max(0, next - Date.now()));
    return () => clearTimeout(timer);
  }, [requests, clock]);

  // A member selected on the map scrolls into view.
  useEffect(() => {
    if (!selectedId || !open) return;
    listRef.current?.querySelector(`[data-user-id="${CSS.escape(selectedId)}"]`)?.scrollIntoView({ block: "nearest" });
  }, [selectedId, open]);

  const requestUpdate = async (userId: string) => {
    setRequests((prev) => ({ ...prev, [userId]: { busy: true, coolUntil: 0, showUntil: 0 } }));
    let outcome: RequestOutcome;
    try {
      const result = await api.POST("/circles/{circleId}/members/{userId}/request-location", {
        params: { path: { circleId, userId } },
      });
      outcome = requestOutcome(result.response.status, result.data, result.response.headers.get("Retry-After"));
    } catch {
      outcome = { kind: "error", message: "Can't reach the server. Check your connection." };
    }
    const state = settled(outcome);
    setRequests((prev) => ({ ...prev, [userId]: state }));
    setClock(state.settledAt);
  };

  const memberItem = (row: MemberRow) => {
    const key = row.location ? addressKey(row.location.lat, row.location.lon) : null;
    const banner = banners.get(row.userId);
    return (
      <MemberItem
        key={row.userId}
        row={row}
        nowMs={nowMs}
        address={key === null ? undefined : addresses.get(key)}
        addressesEnabled={addresses.enabled}
        selected={row.userId === selectedId}
        request={requests[row.userId]}
        clock={clock}
        historyHref={historyPath(circleId, row.userId)}
        placeHref={`/circles/${circleId}/places?new=${encodeURIComponent(row.userId)}`}
        onSelect={() => onSelect(row.userId)}
        onRequest={() => void requestUpdate(row.userId)}
        sos={
          banner && {
            responders: respondersText(banner.responders, banner.ackedBySelf),
            ackedBySelf: banner.ackedBySelf,
            state: sosActionState(sos, banner.alertId),
            ack: () => sos.ack(banner.alertId, banner.name),
          }
        }
      />
    );
  };

  return (
    <aside ref={ref} className={`member-sidebar${open ? "" : " collapsed"}`} aria-label="Circle members">
      <button
        type="button"
        className="member-sidebar-toggle"
        aria-expanded={open}
        aria-controls="member-sidebar-list"
        onClick={() => setOpen((v) => !v)}
      >
        <span>Members</span>
        <span className="muted">{rows.length}</span>
      </button>
      {open && (
        <ul id="member-sidebar-list" className="member-list" ref={listRef}>
          {sidebarEntries(rows, groups).map((entry) =>
            entry.kind === "member" ? (
              memberItem(entry.row)
            ) : (
              <GroupItem
                key={entry.key}
                id={entry.key}
                title={groupTitle(entry.group, rows)}
                stacked={stackedRows(entry.group, rows)}
                freshest={entry.rows.find((row) => row.userId === entry.group.freshestId) ?? null}
                nowMs={nowMs}
                selected={entry.key === selectedId}
                onSelect={() => onSelect(entry.key)}
              >
                {entry.rows.map(memberItem)}
              </GroupItem>
            ),
          )}
        </ul>
      )}
    </aside>
  );
}

/** A group's row: stacked avatars, "Driving together · …" and the freshest fix's age, its members beneath. */
function GroupItem(props: {
  id: string;
  title: string;
  stacked: { shown: MemberRow[]; more: number };
  freshest: MemberRow | null;
  nowMs: number;
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  const at = props.freshest?.location ? Date.parse(props.freshest.location.ts) : null;
  const ago = at === null ? null : (agoText(props.nowMs, at) ?? `on ${new Date(at).toLocaleDateString()}`);
  return (
    <li className={`member-group${props.selected ? " selected" : ""}`} data-user-id={props.id}>
      <button
        type="button"
        className="member-item-main"
        onClick={props.onSelect}
        aria-pressed={props.selected}
        title={props.selected ? "Show everyone on the map" : "Show them on the map"}
      >
        {/* Laid out right to left, so the freshest member (first) is drawn last, on top. */}
        <span className="member-group-avatars" aria-hidden="true">
          {props.stacked.more > 0 && <span className="member-group-more">+{props.stacked.more}</span>}
          {[...props.stacked.shown].reverse().map((row) => (
            <MemberAvatar
              key={row.userId}
              userId={row.userId}
              name={row.name}
              avatarUrl={row.avatarSmallUrl}
              isSelf={row.isSelf}
            />
          ))}
        </span>
        <span className="member-item-text">
          <span className="member-item-name">{props.title}</span>
          {ago && (
            <span className="member-item-meta">
              <span>Located {ago}</span>
            </span>
          )}
        </span>
      </button>
      <ul className="member-group-list" aria-label={props.title}>
        {props.children}
      </ul>
    </li>
  );
}

function MemberItem(props: {
  row: MemberRow;
  nowMs: number;
  /** null: no address there; undefined: unknown so far. */
  address: string | null | undefined;
  addressesEnabled: boolean;
  selected: boolean;
  request: RequestState | undefined;
  clock: number;
  historyHref: string;
  /** Opens a new place at the member's location. */
  placeHref: string;
  onSelect: () => void;
  onRequest: () => void;
  /** Another member's open SOS. */
  sos: { responders: string | null; ackedBySelf: boolean; state: SosActionState; ack: () => void } | undefined;
}) {
  const { row, nowMs, address, request, clock } = props;
  const units = useUnits();
  const name = row.isSelf ? `${row.name} (you)` : row.name;
  // "Location off" explains an old fix better than the stale warning.
  const stale = row.location !== null && !row.locationOff && isStale(row, nowMs);
  const stationary = isStationary(row, nowMs);
  const here = hereSince(row, nowMs);
  const limited = locationLimitText(row);
  const phoneOffPct = phoneMayBeOff(row, nowMs);
  const ago = (at: number) => agoText(nowMs, at) ?? `on ${new Date(at).toLocaleDateString()}`;
  // "Located" is the fix's age; "Last contact" is the phone's, shown when it says more than the fix.
  const located = row.location && !stationary ? ago(Date.parse(row.location.ts)) : null;
  const contactAt = row.location
    ? stale
      ? contactAfterFix(row)
      : null
    : row.lastSeenAt === null
      ? null
      : Date.parse(row.lastSeenAt);
  const approximate = row.bubble && !row.location ? approximateText(row.bubble, nowMs, units) : null;
  const place = row.outsideEvent
    ? "Shared only during the event"
    : row.paused
      ? "Location sharing paused"
      : approximate
        ? null
        : !row.location
          ? row.locationOff
            ? "Location off"
            : (limited ?? "No location yet")
          : address === undefined
            ? props.addressesEnabled
              ? "Finding address…"
              : null
            : (address ?? "Unknown address");
  const battery = row.battery;
  const low = battery !== null && !battery.charging && battery.pct <= LOW_BATTERY_PCT;
  // The server refuses requests for a member sharing an area (403 approximate_sharing).
  const canRequest = !row.isSelf && !row.paused && !row.bubble;
  const sos = props.sos;
  const acked = sos !== undefined && (sos.ackedBySelf || sos.state.done);

  return (
    <li
      className={`member-item${props.selected ? " selected" : ""}${stale ? " stale" : ""}${row.locationOff ? " location-off" : ""}${row.sos ? " sos" : ""}`}
      data-user-id={row.userId}
    >
      <button
        type="button"
        className="member-item-main"
        onClick={props.onSelect}
        aria-pressed={props.selected}
        title={
          props.selected
            ? "Show everyone on the map"
            : row.location || row.bubble
              ? `Show ${row.name} on the map`
              : undefined
        }
      >
        {/* The selected member's row draws a larger photo. */}
        <MemberAvatar
          userId={row.userId}
          name={row.name}
          avatarUrl={props.selected ? row.avatarUrl : row.avatarSmallUrl}
          isSelf={row.isSelf}
        />
        <span className="member-item-text">
          <span className="member-item-name">{name}</span>
          {row.sos && (
            <span className="member-item-sos">
              SOS since {sinceText(nowMs, Date.parse(row.sos.since))}
              {sos?.responders && ` · ${sos.responders}`}
            </span>
          )}
          {approximate && <span className="member-item-place member-item-approximate">{approximate}</span>}
          {place && <span className="member-item-place">{place}</span>}
          <span className="member-item-meta">
            {row.locationOff && (row.location || approximate) && (
              <span className="member-item-location-off">Location off</span>
            )}
            {limited && (row.location || approximate) && (
              <span className="member-item-location-limited">{limited}</span>
            )}
            {phoneOffPct !== null && (
              <span className="member-item-phone-off">Phone may be off (battery {phoneOffPct} %)</span>
            )}
            {here !== null && (
              <span className="member-item-stationary">
                {stationary ? "Stationary · here" : "Here"} since {sinceText(nowMs, here)}
              </span>
            )}
            {located && <span className={stale ? "member-item-stale" : undefined}>Located {located}</span>}
            {contactAt !== null && <span>Last contact {ago(contactAt)}</span>}
            {battery && (
              <span
                className={`member-battery${low ? " low" : ""}`}
                aria-label={`Battery ${battery.pct}%${battery.charging ? ", charging" : ""}`}
              >
                {battery.charging ? "⚡" : "🔋"}
                {battery.pct}%
              </span>
            )}
          </span>
        </span>
      </button>
      <div className="member-item-actions">
        {sos && !acked && (
          <button type="button" className="sos-ack-button" onClick={sos.ack} disabled={sos.state.busy}>
            {sos.state.busy ? "Sending…" : "I'm on it"}
          </button>
        )}
        {sos?.state.message && (
          <span className="member-request-status" role="status">
            {sos.state.message}
          </span>
        )}
        <Link to={props.historyHref} className="member-history-link" aria-label={`${row.name}'s history`}>
          History
        </Link>
        {!row.isSelf && (
          <Link
            to={`/chats?with=${encodeURIComponent(row.userId)}`}
            className="member-message-link"
            aria-label={`Message ${row.name}`}
          >
            Message
          </Link>
        )}
        {row.location && !row.place && (
          <Link
            to={props.placeHref}
            className="member-place-link"
            aria-label={`Save ${row.isSelf ? "your" : `${row.name}'s`} location as a place`}
          >
            Save as place
          </Link>
        )}
        {canRequest && (
          <>
            <button
              type="button"
              className="secondary member-request-button"
              onClick={props.onRequest}
              disabled={request !== undefined && (request.busy || request.coolUntil > clock)}
            >
              {request?.busy ? "Requesting…" : "Request update"}
            </button>
            <span className="member-request-status" role="status">
              {request?.outcome && request.showUntil > clock ? outcomeText(request.outcome, row.name) : ""}
            </span>
          </>
        )}
      </div>
    </li>
  );
}
